'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface AssessmentSection {
  id: string;
  title: string;
  description: string;
  trustServiceCriteria: string;
  questions: Question[];
}

interface Question {
  id: string;
  text: string;
  controlRef?: string;
  weight: number;
}

interface Answers {
  [key: string]: boolean | null;
}

const sections: AssessmentSection[] = [
  {
    id: 'security',
    title: 'Security (Common Criteria)',
    description: 'Protection of system resources against unauthorized access',
    trustServiceCriteria: 'CC',
    questions: [
      { id: 'cc1', text: 'Do you have documented information security policies?', controlRef: 'CC1.1', weight: 10 },
      { id: 'cc2', text: 'Is there a security awareness training program for all employees?', controlRef: 'CC1.4', weight: 8 },
      { id: 'cc3', text: 'Do you perform background checks on employees with access to sensitive systems?', controlRef: 'CC1.4', weight: 7 },
      { id: 'cc4', text: 'Are access rights reviewed and updated regularly?', controlRef: 'CC6.1', weight: 9 },
      { id: 'cc5', text: 'Do you use multi-factor authentication for critical systems?', controlRef: 'CC6.1', weight: 9 },
      { id: 'cc6', text: 'Are network security controls (firewalls, IDS/IPS) in place?', controlRef: 'CC6.6', weight: 9 },
      { id: 'cc7', text: 'Do you encrypt data at rest and in transit?', controlRef: 'CC6.7', weight: 10 },
      { id: 'cc8', text: 'Is there a vulnerability management program?', controlRef: 'CC7.1', weight: 8 },
      { id: 'cc9', text: 'Do you perform regular penetration testing?', controlRef: 'CC4.1', weight: 7 },
      { id: 'cc10', text: 'Is there an incident response plan?', controlRef: 'CC7.3', weight: 9 },
    ],
  },
  {
    id: 'availability',
    title: 'Availability',
    description: 'System accessibility as stipulated in service agreements',
    trustServiceCriteria: 'A',
    questions: [
      { id: 'a1', text: 'Do you have documented SLAs for system availability?', controlRef: 'A1.1', weight: 9 },
      { id: 'a2', text: 'Is there a business continuity plan?', controlRef: 'A1.2', weight: 9 },
      { id: 'a3', text: 'Do you have disaster recovery procedures?', controlRef: 'A1.2', weight: 9 },
      { id: 'a4', text: 'Are backups performed regularly and tested?', controlRef: 'A1.2', weight: 8 },
      { id: 'a5', text: 'Do you monitor system capacity and performance?', controlRef: 'A1.1', weight: 7 },
      { id: 'a6', text: 'Is there redundancy for critical system components?', controlRef: 'A1.2', weight: 8 },
    ],
  },
  {
    id: 'processing-integrity',
    title: 'Processing Integrity',
    description: 'System processing is complete, valid, accurate, timely, and authorized',
    trustServiceCriteria: 'PI',
    questions: [
      { id: 'pi1', text: 'Are there input validation controls for all data entry points?', controlRef: 'PI1.1', weight: 8 },
      { id: 'pi2', text: 'Do you have data quality monitoring processes?', controlRef: 'PI1.2', weight: 7 },
      { id: 'pi3', text: 'Are processing errors detected and corrected timely?', controlRef: 'PI1.3', weight: 8 },
      { id: 'pi4', text: 'Is there an audit trail for data modifications?', controlRef: 'PI1.4', weight: 9 },
      { id: 'pi5', text: 'Do you validate outputs against expected results?', controlRef: 'PI1.5', weight: 7 },
    ],
  },
  {
    id: 'confidentiality',
    title: 'Confidentiality',
    description: 'Information designated as confidential is protected as committed',
    trustServiceCriteria: 'C',
    questions: [
      { id: 'c1', text: 'Is confidential information identified and classified?', controlRef: 'C1.1', weight: 9 },
      { id: 'c2', text: 'Are there access controls limiting access to confidential data?', controlRef: 'C1.1', weight: 9 },
      { id: 'c3', text: 'Is confidential data encrypted when stored and transmitted?', controlRef: 'C1.2', weight: 9 },
      { id: 'c4', text: 'Are there procedures for secure disposal of confidential data?', controlRef: 'C1.2', weight: 7 },
      { id: 'c5', text: 'Do NDAs exist for employees and third parties?', controlRef: 'C1.1', weight: 7 },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy',
    description: 'Personal information is collected, used, retained, and disclosed appropriately',
    trustServiceCriteria: 'P',
    questions: [
      { id: 'p1', text: 'Do you have a published privacy notice?', controlRef: 'P1.1', weight: 9 },
      { id: 'p2', text: 'Is consent obtained before collecting personal information?', controlRef: 'P2.1', weight: 8 },
      { id: 'p3', text: 'Is personal data only used for stated purposes?', controlRef: 'P3.1', weight: 8 },
      { id: 'p4', text: 'Do you have data retention and disposal policies?', controlRef: 'P4.1', weight: 8 },
      { id: 'p5', text: 'Can individuals access and correct their personal data?', controlRef: 'P5.1', weight: 8 },
      { id: 'p6', text: 'Is there a process for handling privacy complaints?', controlRef: 'P8.1', weight: 7 },
    ],
  },
  {
    id: 'risk-management',
    title: 'Risk Management',
    description: 'Risk assessment and management processes',
    trustServiceCriteria: 'CC3',
    questions: [
      { id: 'rm1', text: 'Do you perform regular risk assessments?', controlRef: 'CC3.1', weight: 9 },
      { id: 'rm2', text: 'Is there a documented risk management framework?', controlRef: 'CC3.1', weight: 8 },
      { id: 'rm3', text: 'Are risks documented in a risk register?', controlRef: 'CC3.2', weight: 7 },
      { id: 'rm4', text: 'Do you assess risks from third-party vendors?', controlRef: 'CC3.3', weight: 8 },
      { id: 'rm5', text: 'Are risk mitigation plans implemented and tracked?', controlRef: 'CC3.4', weight: 8 },
    ],
  },
  {
    id: 'change-management',
    title: 'Change Management',
    description: 'Changes to systems are controlled and documented',
    trustServiceCriteria: 'CC8',
    questions: [
      { id: 'cm1', text: 'Is there a formal change management process?', controlRef: 'CC8.1', weight: 9 },
      { id: 'cm2', text: 'Are changes tested before deployment to production?', controlRef: 'CC8.1', weight: 9 },
      { id: 'cm3', text: 'Is there separation of duties between development and production?', controlRef: 'CC8.1', weight: 8 },
      { id: 'cm4', text: 'Are emergency changes documented and reviewed?', controlRef: 'CC8.1', weight: 7 },
      { id: 'cm5', text: 'Do you maintain version control for code and configurations?', controlRef: 'CC8.1', weight: 8 },
    ],
  },
  {
    id: 'vendor-management',
    title: 'Vendor Management',
    description: 'Third-party risk assessment and monitoring',
    trustServiceCriteria: 'CC9',
    questions: [
      { id: 'vm1', text: 'Do you assess vendor security before engagement?', controlRef: 'CC9.2', weight: 9 },
      { id: 'vm2', text: 'Are vendor contracts reviewed for security requirements?', controlRef: 'CC9.2', weight: 8 },
      { id: 'vm3', text: 'Do you monitor vendor compliance regularly?', controlRef: 'CC9.2', weight: 7 },
      { id: 'vm4', text: 'Do you require SOC 2 reports from critical vendors?', controlRef: 'CC9.2', weight: 7 },
      { id: 'vm5', text: 'Is there a process for vendor offboarding?', controlRef: 'CC9.2', weight: 6 },
    ],
  },
];

export default function SOC2ReadinessPage() {
  const [view, setView] = useState<'landing' | 'assessment' | 'results'>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'soc2-readiness',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateResults = () => {
    let totalWeight = 0;
    let earnedWeight = 0;

    const sectionScores = sections.map((section) => {
      let sectionTotal = 0;
      let sectionEarned = 0;

      section.questions.forEach((q) => {
        sectionTotal += q.weight;
        totalWeight += q.weight;
        if (answers[q.id] === true) {
          sectionEarned += q.weight;
          earnedWeight += q.weight;
        }
      });

      return {
        id: section.id,
        title: section.title,
        tsc: section.trustServiceCriteria,
        score: sectionTotal > 0 ? Math.round((sectionEarned / sectionTotal) * 100) : 0,
        gaps: section.questions.filter((q) => answers[q.id] !== true),
      };
    });

    return {
      overallScore: totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0,
      sectionScores,
      readinessLevel: earnedWeight / totalWeight >= 0.8 ? 'Ready' :
                      earnedWeight / totalWeight >= 0.6 ? 'Nearly Ready' :
                      earnedWeight / totalWeight >= 0.4 ? 'In Progress' : 'Early Stage',
    };
  };

  const results = view === 'results' ? calculateResults() : null;

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!results || !commandCenterEnabled) return;

    const gaps = results.sectionScores.flatMap(section =>
      section.gaps.map(gap => ({
        id: gap.id,
        title: gap.text,
        severity: section.score < 40 ? 'critical' as const :
                  section.score < 60 ? 'high' as const :
                  section.score < 80 ? 'medium' as const : 'low' as const,
        description: `Control gap in ${section.title}`,
        recommendation: gap.controlRef ? `Implement control ${gap.controlRef}` : undefined,
      }))
    );

    const recommendations = results.sectionScores
      .filter(s => s.score < 80)
      .sort((a, b) => a.score - b.score)
      .map(section => ({
        id: section.id,
        title: `Improve ${section.title}`,
        priority: section.score < 40 ? 'high' as const :
                  section.score < 60 ? 'high' as const : 'medium' as const,
        description: `Current score: ${section.score}%. Focus on implementing missing controls.`,
        effort: section.gaps.length > 5 ? 'High' : section.gaps.length > 2 ? 'Medium' : 'Low',
      }));

    const scores: Record<string, number> = {};
    results.sectionScores.forEach(s => {
      scores[s.id] = s.score;
    });

    const assessment = await saveResult({
      name: `SOC 2 Readiness - ${new Date().toLocaleDateString()}`,
      score_overall: results.overallScore,
      scores,
      answers,
      gaps,
      recommendations,
      metadata: {
        readinessLevel: results.readinessLevel,
        totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0),
        answeredQuestions: Object.keys(answers).length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const exportReport = () => {
    if (!results) return;

    const lines = [
      '# SOC 2 Readiness Assessment Report',
      '',
      `**Date:** ${new Date().toLocaleDateString()}`,
      `**Overall Readiness:** ${results.overallScore}% (${results.readinessLevel})`,
      '',
      '---',
      '',
      '## Trust Service Criteria Scores',
      '',
      ...results.sectionScores.map((s) => `- **${s.title} (${s.tsc}):** ${s.score}%`),
      '',
      '## Control Gaps by Category',
      '',
    ];

    results.sectionScores.forEach((section) => {
      if (section.gaps.length > 0) {
        lines.push(`### ${section.title}`);
        lines.push('');
        section.gaps.forEach((gap) => {
          lines.push(`- [ ] ${gap.controlRef ? `[${gap.controlRef}] ` : ''}${gap.text}`);
        });
        lines.push('');
      }
    });

    lines.push('## Next Steps');
    lines.push('');
    lines.push('1. Address high-priority control gaps in Security (Common Criteria)');
    lines.push('2. Document all policies and procedures');
    lines.push('3. Implement monitoring and logging');
    lines.push('4. Engage a SOC 2 auditor for readiness assessment');
    lines.push('5. Schedule Type I audit when controls are implemented');
    lines.push('');
    lines.push('---');
    lines.push('*Generated by The Art of CTO - SOC 2 Readiness Tool*');

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soc2-readiness-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-accent';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">SOC 2 Readiness</span>
        </nav>

        {view === 'landing' && (
          <>
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium mb-4">
                AICPA Trust Services Criteria
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
                SOC 2 Readiness Assessment
              </h1>
              <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
                Evaluate your readiness for SOC 2 Type I/II certification across all five
                Trust Service Criteria.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-12">
              {['Security', 'Availability', 'Processing Integrity', 'Confidentiality', 'Privacy'].map((tsc) => (
                <div key={tsc} className="bg-light-surface dark:bg-surface rounded-lg p-4 text-center">
                  <div className="text-2xl mb-2">
                    {tsc === 'Security' && '🔒'}
                    {tsc === 'Availability' && '⚡'}
                    {tsc === 'Processing Integrity' && '✅'}
                    {tsc === 'Confidentiality' && '🔐'}
                    {tsc === 'Privacy' && '👤'}
                  </div>
                  <div className="text-sm font-medium text-light-text dark:text-foreground">{tsc}</div>
                </div>
              ))}
            </div>

            <div className="text-center mb-12">
              <button
                onClick={() => setView('assessment')}
                className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors"
              >
                Start Assessment
              </button>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mt-3">
                50+ controls across 8 categories
              </p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">SOC 2 Audit Types</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Type I</h3>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                    Point-in-time assessment of control design. Evaluates if controls are suitably designed
                    at a specific date.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Type II</h3>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                    Operating effectiveness over time (3-12 months). Evaluates if controls operated
                    effectively during the audit period.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {view === 'assessment' && (
          <>
            <div className="mb-8">
              <div className="flex justify-between text-sm text-light-text-secondary dark:text-foreground-tertiary mb-2">
                <span>{sections[currentSection].title}</span>
                <span>Section {currentSection + 1} of {sections.length}</span>
              </div>
              <div className="h-2 bg-light-border dark:bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-sm font-mono">
                  {sections[currentSection].trustServiceCriteria}
                </span>
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  {sections[currentSection].title}
                </h2>
              </div>
              <p className="text-light-text-secondary dark:text-foreground-secondary mb-8">
                {sections[currentSection].description}
              </p>

              <div className="space-y-6">
                {sections[currentSection].questions.map((question) => (
                  <div key={question.id} className="border-b border-light-border dark:border-border-muted pb-6">
                    <div className="flex items-start gap-2 mb-3">
                      {question.controlRef && (
                        <span className="text-xs px-2 py-1 bg-light-border dark:bg-background text-light-text-secondary dark:text-foreground-tertiary rounded font-mono">
                          {question.controlRef}
                        </span>
                      )}
                      <p className="text-light-text dark:text-foreground">{question.text}</p>
                    </div>
                    <div className="flex gap-4 ml-0">
                      <button
                        onClick={() => handleAnswer(question.id, true)}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          answers[question.id] === true
                            ? 'bg-green-500 text-white'
                            : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-green-500/20'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => handleAnswer(question.id, false)}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          answers[question.id] === false
                            ? 'bg-red-500 text-white'
                            : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-red-500/20'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => currentSection > 0 ? setCurrentSection(currentSection - 1) : setView('landing')}
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                {currentSection > 0 ? '← Previous' : '← Back'}
              </button>
              <button
                onClick={() => {
                  if (currentSection < sections.length - 1) {
                    setCurrentSection(currentSection + 1);
                  } else {
                    setView('results');
                  }
                }}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
              >
                {currentSection < sections.length - 1 ? 'Next Section →' : 'View Results →'}
              </button>
            </div>
          </>
        )}

        {view === 'results' && results && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">SOC 2 Readiness Results</h1>
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(results.overallScore)}`}>
                {results.overallScore}%
              </div>
              <p className={`text-xl ${getScoreColor(results.overallScore)}`}>
                {results.readinessLevel}
              </p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Trust Service Criteria</h2>
              <div className="space-y-4">
                {results.sectionScores.map((section) => (
                  <div key={section.id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-light-text dark:text-foreground">
                        <span className="font-mono text-purple-400 mr-2">{section.tsc}</span>
                        {section.title}
                      </span>
                      <span className={getScoreColor(section.score)}>{section.score}%</span>
                    </div>
                    <div className="h-3 bg-light-border dark:bg-background rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          section.score >= 80 ? 'bg-green-500' :
                          section.score >= 60 ? 'bg-yellow-500' :
                          section.score >= 40 ? 'bg-accent' : 'bg-red-500'
                        }`}
                        style={{ width: `${section.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Priority Controls to Implement</h2>
              {results.sectionScores
                .filter((s) => s.gaps.length > 0)
                .sort((a, b) => a.score - b.score)
                .slice(0, 3)
                .map((section) => (
                  <div key={section.id} className="mb-6 last:mb-0">
                    <h3 className="font-semibold text-accent mb-3">{section.title}</h3>
                    <ul className="space-y-2">
                      {section.gaps.slice(0, 3).map((gap) => (
                        <li key={gap.id} className="flex items-start gap-2 text-sm text-light-text-secondary dark:text-foreground-secondary">
                          <span className="text-red-400 mt-0.5">○</span>
                          <span>
                            {gap.controlRef && <span className="font-mono text-xs mr-2">[{gap.controlRef}]</span>}
                            {gap.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={exportReport}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
              >
                Export Report
              </button>
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
              <button
                onClick={() => {
                  setView('landing');
                  setCurrentSection(0);
                  setAnswers({});
                  setSaved(false);
                }}
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                Start Over
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
